// src/modules/shared/dashboard/BatchKpiContext.tsx
// Provides a single-request cache of all KPI values for a module.
// When a <BatchKpiProvider> wraps the dashboard, each KpiWidget reads from
// this shared map instead of issuing its own individual request.
// When no provider is present the hook returns undefined, and KpiWidget
// falls back to its per-widget fetch (fully backwards compatible).

import { createContext, useContext, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/modules/shared/lib/api-client";
import type { ModuleKey } from "./types";
import type { Period } from "./usePeriodFilter";

type KpiResponse = { value: number; deltaPct: number | null; deltaLabel: string; sparkline?: number[] };
type BatchData = { kpis: Record<string, KpiResponse> };

const BatchContext = createContext<BatchData | null | undefined>(undefined);

/**
 * Returns the KPI entry for the given endpoint path from the batch cache.
 * Returns `undefined` when no BatchKpiProvider is mounted above this call
 * (i.e. per-widget fetching should be used instead).
 */
export function useBatchKpi(endpoint: string): KpiResponse | undefined | null {
  const ctx = useContext(BatchContext);
  // ctx === undefined → no provider; ctx === null → provider mounted but data
  // not yet loaded.
  if (ctx === undefined) return undefined;
  if (ctx === null) return null;
  // Guard against malformed batch response (e.g. in tests that stub all
  // apiFetch calls with a generic payload that has no `.kpis` field).
  if (!ctx.kpis) return null;
  return ctx.kpis[endpoint] ?? null;
}

/** Wrap dashboard content to enable single-shot batch KPI fetching. */
export function BatchKpiProvider({
  module,
  period,
  children,
}: {
  module: ModuleKey;
  period: Period;
  children: ReactNode;
}) {
  const { data } = useQuery<BatchData>({
    queryKey: ["dashboard-batch", module, period],
    queryFn: () =>
      apiFetch<BatchData>(`/${module}/dashboard/kpis-batch?period=${period}`),
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: false,
  });

  // Pass null when data hasn't arrived yet so widgets can show a loading state.
  return (
    <BatchContext.Provider value={data ?? null}>{children}</BatchContext.Provider>
  );
}
